// Analytics data-source connections hub (BI external connections).
//
// Manages the per-project encrypted connection records that Visual Schemas,
// Business Areas, Visuals, Reports and General Processes can read from
// instead of the project's own database — classic RDBMS plus the analytical
// warehouses (Snowflake / Google BigQuery / Dremio via Arrow Flight /
// SAP HANA). Distinct from the Database Designer's "Database connections"
// panel, which controls which database the app itself RUNS on.
//
// ONE implementation, two render modes (spec §5, analytics workbench v2):
//   variant="inline" (default) — the two-pane hub fills its container:
//     left 320px connection list, right connection form / empty state.
//   variant="modal"            — the same hub wrapped in a Modal (kept for
//     ConnectionPicker's "Manage…" escape hatch inside other designers).

import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import Modal from '@/components/common/Modal'
import DeleteObjectDialog from '@/pages/workbench/analytics/DeleteObjectDialog'
import {
  connectionHealthColor,
  deleteBiConnection,
  introspectBiConnection,
  listBiConnections,
  listConnectionTypes,
  saveBiConnection,
  testBiConnection,
  type BiConnection,
  type BiConnectionDraft,
  type ConnectionType,
  type ConnectionTypeCategory,
} from '@/api/connections'
import { listGateways, type Gateway } from '@/api/gateways'
import DatabaseTypePickerModal from './DatabaseTypePickerModal'

const MONO = "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace"

// Badges, labels, sections, default ports, warehouse/read-only status and
// which credential form to draw ALL come from GET /connections/types, which
// derives them from the ONE registry in twasta_runtime_engine/bi_dialect.py.
// There is deliberately no type list, badge map or port map in this file —
// adding a database is a registry entry, not a frontend edit.

// The gateway announces its own dbtype spelling; fold the aliases onto the
// option values above so the auto-filled dropdown isn't left blank.
const DIALECT_ALIASES: Record<string, string> = {
  postgres: 'postgresql', mariadb: 'mysql', sqlserver: 'mssql',
}
const normalizeDialect = (t?: string) => {
  const v = (t || '').trim().toLowerCase()
  return DIALECT_ALIASES[v] || v
}


const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 11px',
  fontSize: 15.5,
  border: '1px solid var(--color-border-strong)',
  borderRadius: 7,
  background: 'var(--color-input-bg)',
  color: 'var(--color-text)',
  fontFamily: 'inherit',
}

const labelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  fontSize: 14.5,
  fontWeight: 600,
  margin: '0 0 5px',
  color: 'var(--color-text-secondary)',
}

const hintStyle: React.CSSProperties = {
  fontSize: 14,
  color: 'var(--color-text-faint)',
  margin: '5px 0 0',
  lineHeight: 1.45,
}

/** Outlined micro-badge marking secrets that are encrypted at rest and
 *  never echoed back by the API. */
function WriteOnlyBadge() {
  return (
    <span
      style={{
        fontSize: 13, fontWeight: 600, letterSpacing: '.04em',
        padding: '1px 7px', borderRadius: 6,
        border: '1px solid var(--color-border-strong)',
        color: 'var(--color-text-faint)',
      }}
    >
      write-only
    </span>
  )
}

/** One cell of the 2-column dynamic field grid. */
function Field({
  label, writeOnly, full, children, hint,
}: {
  label: string
  writeOnly?: boolean
  full?: boolean
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : undefined, minWidth: 0 }}>
      <label style={labelStyle}>
        {label}
        {writeOnly && <WriteOnlyBadge />}
      </label>
      {children}
      {hint && <p style={hintStyle}>{hint}</p>}
    </div>
  )
}

/** Prototype-style toggle switch (42×24 pill). */
function Toggle({
  checked, onChange, label,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
}) {
  return (
    <label
      style={{
        display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
        fontSize: 14.5, fontWeight: 600, color: 'var(--color-text-secondary)',
        minHeight: 40, userSelect: 'none',
      }}
      onClick={() => onChange(!checked)}
    >
      <span
        role="switch"
        aria-checked={checked}
        style={{
          width: 42, height: 24, borderRadius: 999, position: 'relative',
          flexShrink: 0, transition: 'background .15s',
          background: checked ? 'var(--color-accent)' : 'var(--color-border-strong)',
        }}
      >
        <span
          style={{
            position: 'absolute', top: 3, left: checked ? 21 : 3,
            width: 18, height: 18, borderRadius: '50%', background: '#fff',
            transition: 'left .15s',
          }}
        />
      </span>
      {label}
    </label>
  )
}

function emptyDraft(project: string): BiConnectionDraft {
  return { project, CONNECTION_ID: '', CONNECTION_NAME: '', DATABASE_TYPE: 'postgresql', OPTIONS: {} }
}

type TestResult =
  | { state: 'testing' }
  | { state: 'ok'; headline: string; message?: string }
  | { state: 'fail'; headline: string; message?: string }

/** Heuristic: a failed test caused by a missing server-side driver package
 *  gets the spec's dedicated "Driver not installed" headline (§5.3). */
const looksLikeDriverMissing = (msg: string) =>
  /driver|hdbcli|no module|module named|not installed|importerror|pip install/i.test(msg)

// ---------------------------------------------------------------------------
// Browse-tables modal — live schema listing via the introspect API (§11).
// ---------------------------------------------------------------------------

interface BrowseColumn { column?: string; type?: string; nullable?: boolean }
interface BrowseTableDetail {
  table?: string
  is_view?: boolean
  columns?: BrowseColumn[]
  primary_key?: string[]
}

function BrowseTablesModal({
  project, connectionId, connectionName, schema, open, onClose,
}: {
  project: string
  connectionId: string
  connectionName: string
  schema?: string
  open: boolean
  onClose: () => void
}) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [liveSchema, setLiveSchema] = useState('')
  const [tables, setTables] = useState<string[]>([])
  const [views, setViews] = useState<string[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [detail, setDetail] = useState<BrowseTableDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setSelected(null)
    setDetail(null)
    setDetailError(null)
    introspectBiConnection(project, connectionId, schema ? { schema } : undefined)
      .then((res) => {
        if (cancelled) return
        const names = (res.tables || [])
          .map((t) => (typeof t === 'string' ? t : String((t as Record<string, unknown>).table || '')))
          .filter(Boolean)
        setTables(names)
        setViews(res.views || [])
        setLiveSchema(res.schema || '')
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const detail0 = (err as { response?: { data?: { detail?: string } } })
          ?.response?.data?.detail
        setError(detail0 || (err instanceof Error ? err.message : String(err)))
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open, project, connectionId, schema])

  const openTable = async (name: string) => {
    setSelected(name)
    setDetail(null)
    setDetailError(null)
    setDetailLoading(true)
    try {
      const res = await introspectBiConnection(project, connectionId, {
        ...(schema ? { schema } : {}), tables: name,
      })
      const entry = (res.tables || []).find(
        (t) => typeof t !== 'string'
          && String((t as Record<string, unknown>).table || '').toLowerCase() === name.toLowerCase(),
      ) as BrowseTableDetail | undefined
      if (entry) setDetail(entry)
      else setDetailError('No column detail returned for this table.')
    } catch (err: unknown) {
      const detail0 = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail
      setDetailError(detail0 || (err instanceof Error ? err.message : String(err)))
    } finally {
      setDetailLoading(false)
    }
  }

  const allNames: { name: string; isView: boolean }[] = [
    ...tables.map((n) => ({ name: n, isView: false })),
    ...views.map((n) => ({ name: n, isView: true })),
  ]

  return (
    <Modal open={open} onClose={onClose} title="Browse tables" size="full">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0, minHeight: 380 }}>
        <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--color-text-faint)' }}>
          Live schema from {connectionName}{liveSchema ? ` · ${liveSchema}` : ''}
        </p>
        {loading ? (
          <div style={{ padding: 24 }}><div className="spinner" /></div>
        ) : error ? (
          <div
            style={{
              padding: '13px 15px', borderRadius: 10, fontSize: 15,
              background: 'var(--color-error-weak)',
              border: '1px solid var(--color-error)',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Could not list tables</div>
            <div style={{ fontSize: 14, fontFamily: MONO, color: 'var(--color-text-secondary)' }}>
              {error}
            </div>
          </div>
        ) : allNames.length === 0 ? (
          <p style={{ fontSize: 15, color: 'var(--color-text-secondary)' }}>
            No tables or views found{liveSchema ? ` in schema "${liveSchema}"` : ''}.
          </p>
        ) : (
          <div style={{ display: 'flex', gap: 0, minHeight: 0, height: '52vh' }}>
            <div
              style={{
                width: 300, flexShrink: 0, overflowY: 'auto', paddingRight: 12,
                borderRight: '1px solid var(--color-border)',
                display: 'flex', flexDirection: 'column', gap: 4,
              }}
            >
              {allNames.map(({ name, isView }) => (
                <button
                  key={`${isView ? 'v' : 't'}:${name}`}
                  onClick={() => void openTable(name)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '7px 10px', borderRadius: 7, cursor: 'pointer',
                    textAlign: 'left', minWidth: 0,
                    background: selected === name ? 'var(--color-accent-weak)' : 'transparent',
                    border: selected === name
                      ? '1px solid var(--color-accent)'
                      : '1px solid transparent',
                    color: 'var(--color-text)',
                  }}
                >
                  <span
                    style={{
                      flex: 1, minWidth: 0, fontFamily: MONO, fontSize: 14.5,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {name}
                  </span>
                  {isView && (
                    <span style={{ fontSize: 13, color: 'var(--color-text-faint)', flexShrink: 0 }}>
                      view
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', paddingLeft: 18 }}>
              {!selected ? (
                <p style={{ fontSize: 15, color: 'var(--color-text-secondary)', marginTop: 8 }}>
                  Pick a table on the left to see its columns.
                </p>
              ) : detailLoading ? (
                <div style={{ padding: 16 }}><div className="spinner" /></div>
              ) : detailError ? (
                <div
                  style={{
                    padding: '13px 15px', borderRadius: 10, fontSize: 15,
                    background: 'var(--color-error-weak)',
                    border: '1px solid var(--color-error)',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    Could not read columns of {selected}
                  </div>
                  <div style={{ fontSize: 14, fontFamily: MONO, color: 'var(--color-text-secondary)' }}>
                    {detailError}
                  </div>
                </div>
              ) : detail ? (
                <>
                  <div style={{ fontSize: 13, color: 'var(--color-text-faint)', marginBottom: 4 }}>
                    Columns
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 14.5, fontWeight: 600, marginBottom: 10 }}>
                    {detail.table || selected}
                  </div>
                  <div
                    style={{
                      border: '1px solid var(--color-border)', borderRadius: 9,
                      overflow: 'auto',
                    }}
                  >
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14.5 }}>
                      <thead>
                        <tr>
                          {['Column', 'Type', 'Nullable'].map((h) => (
                            <th
                              key={h}
                              style={{
                                textAlign: 'left', padding: '8px 12px',
                                fontFamily: MONO, fontWeight: 600,
                                background: 'var(--color-surface-2)',
                                borderBottom: '1px solid var(--color-border)',
                                color: 'var(--color-text-secondary)',
                              }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(detail.columns || []).map((c) => (
                          <tr key={c.column}>
                            <td
                              style={{
                                padding: '7px 12px', fontFamily: MONO,
                                borderBottom: '1px solid var(--color-border)',
                              }}
                            >
                              {c.column}
                              {(detail.primary_key || []).includes(c.column || '') && (
                                <span
                                  style={{
                                    marginLeft: 8, fontSize: 13, fontWeight: 600,
                                    padding: '1px 6px', borderRadius: 5,
                                    background: 'var(--color-info-weak)',
                                    color: 'var(--color-info)',
                                  }}
                                >
                                  PK
                                </span>
                              )}
                            </td>
                            <td
                              style={{
                                padding: '7px 12px', fontFamily: MONO,
                                color: 'var(--color-text-secondary)',
                                borderBottom: '1px solid var(--color-border)',
                              }}
                            >
                              {c.type}
                            </td>
                            <td
                              style={{
                                padding: '7px 12px', fontFamily: MONO,
                                color: 'var(--color-text-faint)',
                                borderBottom: '1px solid var(--color-border)',
                              }}
                            >
                              {c.nullable === false ? 'not null' : 'null'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// The hub itself.
// ---------------------------------------------------------------------------

export default function BiConnectionsPanel({
  project, open = true, onClose, onChanged, variant = 'inline', initialNew = false,
  onAttach, attachLabel = 'Use this connection',
}: {
  project: string
  /** Modal variant only — whether the modal is shown. Inline ignores it. */
  open?: boolean
  /** Modal variant only. */
  onClose?: () => void
  /** Fired after any save/delete/test so pickers can refresh their options.
   *  A save passes the stored record (no secrets) so callers can auto-select
   *  the connection that was just created. */
  onChanged?: (saved?: BiConnection) => void
  variant?: 'inline' | 'modal'
  /** Open with the blank new-connection form already showing ("create a
   *  connection" entry points — the Add-source modal, the agent's
   *  provide-a-connection round). */
  initialNew?: boolean
  /** "Pick a connection" mode: the panel is being used to CHOOSE a source
   *  (the agent's provide-a-connection round), not just to administer the
   *  list. Renders an explicit attach button on the open form AND on every
   *  existing row, so selecting a connection is a deliberate click rather
   *  than a side effect of dismissing the panel. */
  onAttach?: (conn: BiConnection) => void
  /** Caption of that button. */
  attachLabel?: string
}) {
  const [connections, setConnections] = useState<BiConnection[]>([])
  const [loading, setLoading] = useState(false)
  const [draft, setDraft] = useState<BiConnectionDraft | null>(null)
  const [isNew, setIsNew] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<TestResult | null>(null)
  const [browseOpen, setBrowseOpen] = useState(false)
  // Open connection targeted by the dependency-checked delete dialog.
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; name: string } | null>(null)
  // Registered gateways (loaded lazily when the Gateway type is picked) — the
  // gateway form binds to a gateway + one of its announced database aliases.
  const [gateways, setGateways] = useState<Gateway[] | null>(null)
  const [gatewaysLoading, setGatewaysLoading] = useState(false)
  // The database-type catalogue (labels, badges, sections, default ports,
  // driver availability, tenant usage counts) — one fetch, shared by the
  // picker modal, the list badges and the credential form.
  const [catalog, setCatalog] = useState<ConnectionType[]>([])
  const [categories, setCategories] = useState<ConnectionTypeCategory[]>([])
  const [catalogLoading, setCatalogLoading] = useState(false)
  const [catalogError, setCatalogError] = useState<string | null>(null)
  // The instance-admin ceiling on a connection's own query timeout, served
  // alongside the catalogue — a project user cannot read the admin setting
  // itself. 600 is the built-in default the backend also falls back to.
  const [maxQueryTimeout, setMaxQueryTimeout] = useState(600)
  const [pickerOpen, setPickerOpen] = useState(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      setConnections(await listBiConnections(project))
    } catch (err) {
      toast.error(`Could not load connections: ${err instanceof Error ? err.message : err}`)
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => {
    if (open) void refresh()
  }, [open, refresh])

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true)
    setCatalogError(null)
    try {
      const { types, categories: cats, max_query_timeout_seconds } = await listConnectionTypes()
      setCatalog(types)
      setCategories(cats)
      setMaxQueryTimeout(max_query_timeout_seconds)
    } catch (err) {
      // The panel still works without the catalogue (the form falls back to
      // the classic shape), so this degrades rather than blocking the hub.
      setCatalogError(
        `Could not load the database catalogue: ${err instanceof Error ? err.message : err}`)
    } finally {
      setCatalogLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open) void loadCatalog()
  }, [open, loadCatalog])

  // Refresh the catalogue after a save/delete so the "in use" counts (and the
  // picker's pinned section) reflect what the tenant actually has.
  const typeOf = useCallback(
    (dbType?: string): ConnectionType | undefined =>
      catalog.find((x) => x.value === (dbType || 'postgresql').toLowerCase()),
    [catalog],
  )
  const typeLabel = useCallback(
    (dbType?: string) => typeOf(dbType)?.label || dbType || 'Database',
    [typeOf],
  )
  // Every type twasta can connect to directly is frontable by a gateway —
  // the registry says which (only "gateway" itself is excluded: no gateway
  // behind a gateway).
  const gatewayRemoteDialects = useMemo(
    () => catalog.filter((t) => t.gateway_frontable),
    [catalog],
  )

  const loadGateways = useCallback(async () => {
    setGatewaysLoading(true)
    try {
      setGateways(await listGateways())
    } catch {
      setGateways([])
    } finally {
      setGatewaysLoading(false)
    }
  }, [])

  // Lazy-load the registered gateways the first time a gateway connection is
  // opened/selected (the picker needs them; other types never pay the cost).
  useEffect(() => {
    if (draft && (draft.DATABASE_TYPE || '').toLowerCase() === 'gateway'
        && gateways === null && !gatewaysLoading) {
      void loadGateways()
    }
  }, [draft, gateways, gatewaysLoading, loadGateways])

  const patch = (p: Partial<BiConnectionDraft>) =>
    setDraft((d) => (d ? { ...d, ...p } : d))
  const patchOpt = (key: string, value: unknown) =>
    setDraft((d) => (d ? { ...d, OPTIONS: { ...(d.OPTIONS || {}), [key]: value } } : d))
  const optStr = (key: string): string => {
    const v = ((draft?.OPTIONS || {}))[key]
    return v === undefined || v === null ? '' : String(v)
  }

  const startEdit = (c: BiConnection) => {
    setIsNew(false)
    setTestResult(null)
    setDraft({ project, OPTIONS: {}, ...c })
  }

  const startNew = () => {
    setIsNew(true)
    setTestResult(null)
    setDraft(emptyDraft(project))
  }

  // "Create a connection" entry points land straight on the blank form.
  useEffect(() => {
    if (open && initialNew) startNew()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialNew])

  /** Changing type on a NEW connection resets the type-specific values (per
   *  spec §5.2); on an EXISTING one only the type flips, so a mis-click
   *  never wipes saved settings. The test result always resets. */
  const changeType = (newType: string) => {
    setTestResult(null)
    setPickerOpen(false)
    setDraft((d) => {
      if (!d) return d
      if (isNew) {
        return {
          project: d.project, CONNECTION_ID: d.CONNECTION_ID,
          CONNECTION_NAME: d.CONNECTION_NAME, DATABASE_TYPE: newType, OPTIONS: {},
          // Type-agnostic, so it survives a type change like the id/name do.
          QUERY_TIMEOUT_SECONDS: d.QUERY_TIMEOUT_SECONDS,
        }
      }
      return { ...d, DATABASE_TYPE: newType }
    })
  }

  const validateDraft = (): string | null => {
    if (!draft) return 'No connection form is open'
    if (!(draft.CONNECTION_ID || '').trim()) {
      return 'Connection ID is required (letters/digits/_/-)'
    }
    // Type-agnostic, so it is checked before any per-type branch returns. The
    // server enforces the same ceiling — this only fails faster.
    const qt = draft.QUERY_TIMEOUT_SECONDS
    if (qt !== undefined && qt !== null) {
      if (!Number.isFinite(qt) || qt < 1) {
        return 'Query timeout must be at least 1 second (leave blank for the instance default)'
      }
      if (qt > maxQueryTimeout) {
        return `Query timeout cannot exceed the instance maximum of ${maxQueryTimeout} seconds`
      }
    }
    const dt = (draft.DATABASE_TYPE || 'postgresql').toLowerCase()
    const missing = (val: unknown, label: string): string | null =>
      String(val ?? '').trim() ? null : `${label} is required`
    if (dt === 'sqlite') {
      return missing(draft.DATABASE, 'Database file path')
    }
    if (dt === 'gateway') {
      const opt = (draft.OPTIONS || {}) as Record<string, unknown>
      return (
        missing(opt.GATEWAY_ID, 'Gateway')
        || missing(opt.DATABASE_ALIAS, 'Database alias')
        || missing(opt.REMOTE_DIALECT, 'Remote database type')
      )
    }
    const opt = (draft.OPTIONS || {}) as Record<string, unknown>
    if (dt === 'databricks') {
      // HTTP_PATH is the one people forget, and without it the driver
      // connects to nothing rather than erroring usefully.
      return (
        missing(draft.HOST, 'Workspace host')
        || missing(opt.HTTP_PATH, 'HTTP path')
        || (isNew && !String(opt.CLIENT_ID ?? '').trim()
          ? missing(draft.PASSWORD, 'Access token') : null)
      )
    }
    if (dt === 'athena') {
      const staging = String(opt.S3_STAGING_DIR ?? '').trim()
      if (staging && !staging.startsWith('s3://')) {
        return 'S3 staging directory must be an s3:// URI'
      }
      return (
        missing(opt.REGION, 'AWS region')
        || missing(opt.S3_STAGING_DIR, 'S3 staging directory')
      )
    }
    if (dt === 'trino') {
      // Schema is not optional: with only a catalog the session has no default
      // schema and every unqualified table name fails.
      return (
        missing(draft.HOST, 'Host')
        || missing(opt.CATALOG, 'Catalog')
        || missing(draft.SCHEMA, 'Schema')
      )
    }
    if (dt === 'clickhouse') {
      return missing(draft.HOST, 'Host')
    }
    if (dt === 'duckdb') {
      return missing(draft.DATABASE, 'Database')
    }
    // Everything on the classic host/credentials/database form — including
    // every wire-compatible variant (Redshift, CockroachDB, Synapse, …), which
    // is why this asks the catalogue rather than listing types.
    //
    // If the catalogue failed to load we fall back to the four types that were
    // always classic, rather than defaulting to 'classic' — otherwise Snowflake
    // (whose account lives in OPTIONS, with no HOST at all) would be rejected
    // for a missing host it does not have.
    const form = typeOf(dt)?.form
      ?? (['postgresql', 'mysql', 'oracle', 'mssql'].includes(dt) ? 'classic' : '')
    if (form === 'classic') {
      const dbLabel = dt === 'oracle'
        ? (draft.ORACLE_CONNECT_TYPE === 'sid' ? 'SID' : 'Service name')
        : 'Database'
      return (
        missing(draft.HOST, 'Host')
        || missing(draft.USERNAME, 'Username')
        || (isNew ? missing(draft.PASSWORD, 'Password') : null)
        || missing(draft.DATABASE, dbLabel)
      )
    }
    return null
  }

  /** Persist the open draft. Returns the STORED record (secrets stripped) so
   *  callers can hand the exact connection to whoever is waiting for it, or
   *  null when validation/save failed. */
  const doSave = async (): Promise<BiConnection | null> => {
    if (!draft) return null
    const problem = validateDraft()
    if (problem) {
      toast.error(problem)
      return null
    }
    const cid = (draft.CONNECTION_ID || '').trim()
    try {
      const stored = await saveBiConnection({ ...draft, CONNECTION_ID: cid })
      onChanged?.(stored)
      await refresh()
      // The picker's "in use" counts just moved — reload the catalogue so the
      // pinned section reflects what the tenant now has.
      void loadCatalog()
      return stored ?? null
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail
      toast.error(detail || `Save failed: ${err instanceof Error ? err.message : err}`)
      return null
    }
  }

  /** "Save connection" — persist + close the form (spec §5.4). */
  const handleSave = async () => {
    if (!draft) return
    setSaving(true)
    try {
      const stored = await doSave()
      if (stored) {
        toast.success(
          `Connection "${draft.CONNECTION_NAME || stored.CONNECTION_ID}" saved`)
        setDraft(null)
        setTestResult(null)
      }
    } finally {
      setSaving(false)
    }
  }

  /** Pick-a-connection mode — "Use this connection": persist whatever is on
   *  the form (so the id the caller reports really exists), then hand the
   *  stored record over. */
  const handleAttach = async () => {
    if (!draft || !onAttach) return
    setSaving(true)
    try {
      const stored = await doSave()
      if (stored) onAttach(stored)
    } finally {
      setSaving(false)
    }
  }

  /** "Test connection" — saves the draft first (the backend tests saved
   *  records), then runs the test and shows the §5.3 result box inline.
   *  Keeps the form open so a failure can be fixed immediately. */
  const handleTest = async () => {
    if (!draft) return
    const problem = validateDraft()
    if (problem) {
      toast.error(problem)
      return
    }
    setTesting(true)
    setTestResult({ state: 'testing' })
    try {
      const stored = await doSave()
      if (!stored) {
        setTestResult(null)
        return
      }
      const cid = stored.CONNECTION_ID
      if (isNew) setIsNew(false) // it exists now — ID becomes fixed
      try {
        const res = await testBiConnection(project, cid)
        if (res.success) {
          setTestResult({
            state: 'ok',
            headline: 'Connection successful',
            message: res.message || 'Read access verified.',
          })
        } else {
          const msg = res.error || 'Connection test failed.'
          setTestResult({
            state: 'fail',
            headline: looksLikeDriverMissing(msg) ? 'Driver not installed' : 'Could not connect',
            message: msg,
          })
        }
      } catch (err: unknown) {
        const detail = (err as { response?: { data?: { detail?: string } } })
          ?.response?.data?.detail
        const msg = detail || (err instanceof Error ? err.message : String(err))
        setTestResult({
          state: 'fail',
          headline: looksLikeDriverMissing(msg) ? 'Driver not installed' : 'Could not connect',
          message: msg,
        })
      }
      // The backend stamped LAST_TEST — reload so the health dots update
      // (here and in the embedding section/overview).
      await refresh()
      onChanged?.()
    } finally {
      setTesting(false)
    }
  }

  // Open the dependency-checked delete dialog. It refuses (and lists the
  // dependents) when a schema / business area / visual / general process
  // still targets this connection; only an unused connection is deletable.
  const handleDelete = () => {
    if (!draft || isNew) return
    setConfirmDelete({
      id: draft.CONNECTION_ID,
      name: draft.CONNECTION_NAME || draft.CONNECTION_ID,
    })
  }

  const t = (draft?.DATABASE_TYPE || 'postgresql').toLowerCase()
  // Read-only analytical source — the registry decides, not a list here, so
  // ClickHouse/Databricks/Trino/Redshift are classified without an edit.
  const isWarehouse = Boolean(typeOf(t)?.warehouse)
  const busy = saving || testing

  // ---- per-type dynamic field grid (spec §5.2 + every previously shipped
  // field: Oracle connect type, optional schemas, OPTIONS extras,
  // max-bytes-billed, Dremio space path, HANA tenant DB). -------------------

  const passwordField = (
    <Field label="Password" writeOnly>
      <input
        style={inputStyle}
        type="password"
        value={draft?.PASSWORD ?? ''}
        placeholder={isNew ? '' : '(unchanged)'}
        onChange={(e) => patch({ PASSWORD: e.target.value })}
      />
    </Field>
  )

  const usernameField = (
    <Field label="Username">
      <input
        style={inputStyle}
        value={draft?.USERNAME || ''}
        onChange={(e) => patch({ USERNAME: e.target.value })}
      />
    </Field>
  )

  const renderGatewayFields = () => {
    const selectedGatewayId = optStr('GATEWAY_ID')
    const selectedGateway = (gateways || []).find((g) => g.id === selectedGatewayId)
    const aliases = selectedGateway?.databases || []
    return (
      <>
        <Field
          label="Gateway"
          full
          hint="On-prem gateways your tenant has paired. Manage them in Enterprise → Gateways. No host or credentials are stored here — the gateway holds those inside your network."
        >
          <select
            style={inputStyle}
            value={selectedGatewayId}
            onChange={(e) => {
              // Selecting a gateway clears the alias/dialect (they belong to
              // the chosen gateway).
              patch({ OPTIONS: { ...(draft?.OPTIONS || {}), GATEWAY_ID: e.target.value, DATABASE_ALIAS: '', REMOTE_DIALECT: '' } })
            }}
          >
            <option value="">
              {gatewaysLoading ? 'Loading gateways…' : '— select a gateway —'}
            </option>
            {(gateways || []).map((g) => (
              <option key={g.id} value={g.id}>
                {g.name} {g.online ? '● online' : '○ offline'}
                {g.status !== 'active' ? ` (${g.status})` : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Database alias"
          hint={
            aliases.length
              ? 'A database the gateway advertises.'
              : selectedGatewayId
                ? 'This gateway has not announced any databases yet — configure one in the gateway app and let it reconnect.'
                : 'Pick a gateway first.'
          }
        >
          <select
            style={inputStyle}
            value={optStr('DATABASE_ALIAS')}
            disabled={!selectedGatewayId}
            onChange={(e) => {
              const alias = aliases.find((a) => a.alias === e.target.value)
              // Auto-fill the remote dialect from the announced alias metadata
              // when available (SQL correctness depends on it).
              const nextOpts: Record<string, unknown> = {
                ...(draft?.OPTIONS || {}),
                DATABASE_ALIAS: e.target.value,
              }
              if (alias?.dbtype) nextOpts.REMOTE_DIALECT = normalizeDialect(alias.dbtype)
              patch({ OPTIONS: nextOpts })
            }}
          >
            <option value="">— select a database —</option>
            {aliases.map((a) => (
              <option key={a.alias} value={a.alias}>
                {a.alias}{a.label ? ` — ${a.label}` : ''}{a.dbtype ? ` (${a.dbtype})` : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Remote database type"
          hint="The dialect twasta generates SQL for. Auto-filled from the alias when the gateway reports it."
        >
          <select
            style={inputStyle}
            value={optStr('REMOTE_DIALECT')}
            onChange={(e) => patchOpt('REMOTE_DIALECT', e.target.value)}
          >
            <option value="">— select —</option>
            {gatewayRemoteDialects.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Schema (optional)">
          <input
            style={inputStyle}
            value={draft?.SCHEMA || ''}
            placeholder="e.g. analytics"
            onChange={(e) => patch({ SCHEMA: e.target.value })}
          />
        </Field>
      </>
    )
  }

  const renderTypeFields = () => {
    if (!draft) return null

    if (t === 'gateway') return renderGatewayFields()

    if (t === 'sqlite') {
      return (
        <Field label="Database file path" full>
          <input
            style={inputStyle}
            value={draft.DATABASE || ''}
            placeholder="/data/analytics.db"
            onChange={(e) => patch({ DATABASE: e.target.value })}
          />
        </Field>
      )
    }

    if (t === 'snowflake') {
      return (
        <>
          <Field label="Account identifier">
            <input
              style={inputStyle}
              value={optStr('ACCOUNT')}
              placeholder="xy12345.eu-central-1"
              onChange={(e) => patchOpt('ACCOUNT', e.target.value)}
            />
          </Field>
          <Field label="Warehouse">
            <input
              style={inputStyle}
              value={optStr('WAREHOUSE')}
              placeholder="ANALYTICS_WH"
              onChange={(e) => patchOpt('WAREHOUSE', e.target.value)}
            />
          </Field>
          <Field label="Role (optional)">
            <input
              style={inputStyle}
              value={optStr('ROLE')}
              placeholder="ANALYST"
              onChange={(e) => patchOpt('ROLE', e.target.value)}
            />
          </Field>
          <Field label="Database">
            <input
              style={inputStyle}
              value={draft.DATABASE || ''}
              placeholder="PHARMA_DW"
              onChange={(e) => patch({ DATABASE: e.target.value })}
            />
          </Field>
          <Field label="Schema">
            <input
              style={inputStyle}
              value={draft.SCHEMA || ''}
              placeholder="PUBLIC"
              onChange={(e) => patch({ SCHEMA: e.target.value })}
            />
          </Field>
          {usernameField}
          {passwordField}
          <Field label="Private key (PEM) — or use password" writeOnly full>
            <textarea
              style={{ ...inputStyle, minHeight: 84, fontFamily: MONO, resize: 'vertical' }}
              rows={4}
              value={draft.PRIVATE_KEY ?? ''}
              placeholder={isNew
                ? '-----BEGIN PRIVATE KEY----- … (leave empty for password auth)'
                : '(unchanged — paste to replace)'}
              onChange={(e) => patch({ PRIVATE_KEY: e.target.value })}
            />
          </Field>
        </>
      )
    }

    if (t === 'bigquery') {
      return (
        <>
          <Field label="GCP project id">
            <input
              style={inputStyle}
              value={optStr('PROJECT_ID')}
              placeholder="pharma-analytics-prod"
              onChange={(e) => patchOpt('PROJECT_ID', e.target.value)}
            />
          </Field>
          <Field label="Dataset">
            <input
              style={inputStyle}
              value={optStr('DATASET')}
              placeholder="warehouse"
              onChange={(e) => patchOpt('DATASET', e.target.value)}
            />
          </Field>
          <Field label="Service account JSON" writeOnly full>
            <textarea
              style={{ ...inputStyle, minHeight: 96, fontFamily: MONO, resize: 'vertical' }}
              rows={4}
              value={draft.SERVICE_ACCOUNT_JSON ?? ''}
              placeholder={isNew
                ? '{ "type": "service_account", ... }'
                : '(unchanged — paste to replace)'}
              onChange={(e) => patch({ SERVICE_ACCOUNT_JSON: e.target.value })}
            />
          </Field>
          <Field
            label="Max bytes billed (cost cap, optional)"
            hint="Queries above this byte estimate are refused by BigQuery."
          >
            <input
              style={inputStyle}
              type="number"
              value={optStr('MAX_BYTES_BILLED')}
              placeholder="2000000000"
              onChange={(e) => patchOpt('MAX_BYTES_BILLED', e.target.value ? Number(e.target.value) : '')}
            />
          </Field>
        </>
      )
    }

    if (t === 'dremio') {
      return (
        <>
          <Field label="Host">
            <input
              style={inputStyle}
              value={draft.HOST || ''}
              placeholder="dremio.internal.pharma.co"
              onChange={(e) => patch({ HOST: e.target.value })}
            />
          </Field>
          <Field label="Arrow Flight port">
            <input
              style={inputStyle}
              type="number"
              value={optStr('FLIGHT_PORT')}
              placeholder="32010"
              onChange={(e) => patchOpt('FLIGHT_PORT', e.target.value ? Number(e.target.value) : '')}
            />
          </Field>
          <Field label="">
            <Toggle
              checked={!!(draft.OPTIONS || {}).TLS}
              onChange={(v) => patchOpt('TLS', v)}
              label="Use TLS"
            />
          </Field>
          <Field label="Space / folder path (optional schema)">
            <input
              style={inputStyle}
              value={draft.SCHEMA || ''}
              placeholder='e.g. Samples."samples.dremio.com"'
              onChange={(e) => patch({ SCHEMA: e.target.value })}
            />
          </Field>
          {usernameField}
          {passwordField}
        </>
      )
    }

    if (t === 'hana') {
      return (
        <>
          <Field label="Host">
            <input
              style={inputStyle}
              value={draft.HOST || ''}
              onChange={(e) => patch({ HOST: e.target.value })}
            />
          </Field>
          <Field label="Port">
            <input
              style={inputStyle}
              type="number"
              value={draft.PORT ?? ''}
              placeholder="30015 (cloud: 443)"
              onChange={(e) => patch({ PORT: e.target.value ? Number(e.target.value) : null })}
            />
          </Field>
          <Field label="">
            <Toggle
              checked={(draft.OPTIONS || {}).ENCRYPT === undefined
                ? draft.PORT === 443
                : !!(draft.OPTIONS || {}).ENCRYPT}
              onChange={(v) => patchOpt('ENCRYPT', v)}
              label="Encrypt connection"
            />
          </Field>
          <Field label="Tenant database (optional)">
            <input
              style={inputStyle}
              value={draft.DATABASE || ''}
              placeholder="HXE"
              onChange={(e) => patch({ DATABASE: e.target.value })}
            />
          </Field>
          {usernameField}
          {passwordField}
          <Field label="Schema (optional)">
            <input
              style={inputStyle}
              value={draft.SCHEMA || ''}
              onChange={(e) => patch({ SCHEMA: e.target.value })}
            />
          </Field>
        </>
      )
    }

    if (t === 'databricks') {
      return (
        <>
          <Field label="Workspace host" full>
            <input
              style={inputStyle}
              value={draft.HOST || ''}
              placeholder="dbc-1234abcd-5678.cloud.databricks.com"
              onChange={(e) => patch({ HOST: e.target.value })}
            />
          </Field>
          <Field label="HTTP path" full
                 hint="The SQL warehouse's HTTP path — Databricks → SQL Warehouses → Connection details.">
            <input
              style={{ ...inputStyle, fontFamily: MONO }}
              value={optStr('HTTP_PATH')}
              placeholder="/sql/1.0/warehouses/abc123def456"
              onChange={(e) => patchOpt('HTTP_PATH', e.target.value)}
            />
          </Field>
          <Field label="Catalog" hint="Unity Catalog name (tables are catalog.schema.table).">
            <input
              style={inputStyle}
              value={optStr('CATALOG')}
              placeholder="main"
              onChange={(e) => patchOpt('CATALOG', e.target.value)}
            />
          </Field>
          <Field label="Schema">
            <input
              style={inputStyle}
              value={draft.SCHEMA || ''}
              placeholder="default"
              onChange={(e) => patch({ SCHEMA: e.target.value })}
            />
          </Field>
          <Field label="Access token" writeOnly full
                 hint="A personal access token (dapi…). For OAuth machine-to-machine, set the client id below and put the client secret here instead.">
            <input
              style={inputStyle}
              type="password"
              value={draft.PASSWORD ?? ''}
              placeholder={isNew ? 'dapi…' : '(unchanged)'}
              onChange={(e) => patch({ PASSWORD: e.target.value })}
            />
          </Field>
          <Field label="OAuth client id (optional)" full
                 hint="Set only for service-principal (M2M) auth.">
            <input
              style={inputStyle}
              value={optStr('CLIENT_ID')}
              onChange={(e) => patchOpt('CLIENT_ID', e.target.value)}
            />
          </Field>
        </>
      )
    }

    if (t === 'clickhouse') {
      const protocol = optStr('PROTOCOL') || 'native'
      return (
        <>
          <Field label="Host">
            <input
              style={inputStyle}
              value={draft.HOST || ''}
              placeholder="clickhouse.internal"
              onChange={(e) => patch({ HOST: e.target.value })}
            />
          </Field>
          <Field label="Protocol">
            <select
              style={{ ...inputStyle, cursor: 'pointer' }}
              value={protocol}
              onChange={(e) => patchOpt('PROTOCOL', e.target.value)}
            >
              <option value="native">Native (TCP)</option>
              <option value="http">HTTP</option>
            </select>
          </Field>
          <Field label="Port">
            <input
              style={inputStyle}
              type="number"
              value={draft.PORT ?? ''}
              placeholder={protocol === 'http' ? '8123 (TLS: 8443)' : '9000 (TLS: 9440)'}
              onChange={(e) => patch({ PORT: e.target.value ? Number(e.target.value) : null })}
            />
          </Field>
          <Field label="Database">
            <input
              style={inputStyle}
              value={draft.DATABASE || ''}
              placeholder="default"
              onChange={(e) => patch({ DATABASE: e.target.value })}
            />
          </Field>
          <Field label="">
            <Toggle
              checked={(draft.OPTIONS || {}).SECURE === undefined
                ? draft.PORT === 9440 || draft.PORT === 8443
                : !!(draft.OPTIONS || {}).SECURE}
              onChange={(v) => patchOpt('SECURE', v)}
              label="TLS"
            />
          </Field>
          {usernameField}
          {passwordField}
          <p style={{ ...hintStyle, gridColumn: '1 / -1', margin: 0 }}>
            ClickHouse identifiers are <strong>case-sensitive</strong> — table
            and column names must be written exactly as they exist.
          </p>
        </>
      )
    }

    if (t === 'trino') {
      return (
        <>
          <Field label="Host">
            <input
              style={inputStyle}
              value={draft.HOST || ''}
              placeholder="trino.internal"
              onChange={(e) => patch({ HOST: e.target.value })}
            />
          </Field>
          <Field label="Port">
            <input
              style={inputStyle}
              type="number"
              value={draft.PORT ?? ''}
              placeholder="8080 (TLS: 443)"
              onChange={(e) => patch({ PORT: e.target.value ? Number(e.target.value) : null })}
            />
          </Field>
          <Field label="Catalog"
                 hint="Required — a bare table name only resolves inside one catalog.">
            <input
              style={inputStyle}
              value={optStr('CATALOG')}
              placeholder="hive"
              onChange={(e) => patchOpt('CATALOG', e.target.value)}
            />
          </Field>
          <Field label="Schema">
            <input
              style={inputStyle}
              value={draft.SCHEMA || ''}
              placeholder="sales"
              onChange={(e) => patch({ SCHEMA: e.target.value })}
            />
          </Field>
          {usernameField}
          <Field label="Password (optional)" writeOnly
                 hint="Set for BASIC auth over TLS; leave blank for an unauthenticated cluster.">
            <input
              style={inputStyle}
              type="password"
              value={draft.PASSWORD ?? ''}
              placeholder={isNew ? '' : '(unchanged)'}
              onChange={(e) => patch({ PASSWORD: e.target.value })}
            />
          </Field>
        </>
      )
    }

    if (t === 'athena') {
      return (
        <>
          <Field label="AWS region">
            <input
              style={inputStyle}
              value={optStr('REGION')}
              placeholder="eu-west-1"
              onChange={(e) => patchOpt('REGION', e.target.value)}
            />
          </Field>
          <Field label="Glue database">
            <input
              style={inputStyle}
              value={draft.SCHEMA || ''}
              placeholder="default"
              onChange={(e) => patch({ SCHEMA: e.target.value })}
            />
          </Field>
          <Field label="S3 staging directory" full
                 hint="Where Athena writes query results. Required.">
            <input
              style={{ ...inputStyle, fontFamily: MONO }}
              value={optStr('S3_STAGING_DIR')}
              placeholder="s3://my-bucket/athena-results/"
              onChange={(e) => patchOpt('S3_STAGING_DIR', e.target.value)}
            />
          </Field>
          <Field label="Workgroup (optional)">
            <input
              style={inputStyle}
              value={optStr('WORKGROUP')}
              placeholder="primary"
              onChange={(e) => patchOpt('WORKGROUP', e.target.value)}
            />
          </Field>
          <Field label="Access key id (optional)"
                 hint="Leave both blank to use the server's AWS role / environment credentials.">
            <input
              style={inputStyle}
              value={draft.USERNAME || ''}
              onChange={(e) => patch({ USERNAME: e.target.value })}
            />
          </Field>
          <Field label="Secret access key (optional)" writeOnly>
            <input
              style={inputStyle}
              type="password"
              value={draft.PASSWORD ?? ''}
              placeholder={isNew ? '' : '(unchanged)'}
              onChange={(e) => patch({ PASSWORD: e.target.value })}
            />
          </Field>
          <p style={{ ...hintStyle, gridColumn: '1 / -1', margin: 0 }}>
            Athena is billed by bytes scanned — prefer partitioned tables and
            narrow column lists.
          </p>
        </>
      )
    }

    if (t === 'duckdb') {
      return (
        <>
          <Field label="Database" full
                 hint="A file path on the server, or a MotherDuck database name when a token is set.">
            <input
              style={{ ...inputStyle, fontFamily: MONO }}
              value={draft.DATABASE || ''}
              placeholder="/data/analytics.duckdb  —  or  my_db (MotherDuck)"
              onChange={(e) => patch({ DATABASE: e.target.value })}
            />
          </Field>
          <Field label="MotherDuck token (optional)" writeOnly full
                 hint="Set to use the hosted MotherDuck service instead of a local file.">
            <input
              style={inputStyle}
              type="password"
              value={draft.PASSWORD ?? ''}
              placeholder={isNew ? '' : '(unchanged)'}
              onChange={(e) => patch({ PASSWORD: e.target.value })}
            />
          </Field>
        </>
      )
    }

    // Classic RDBMS: postgresql / mysql / oracle / mssql
    const portPlaceholder = String(typeOf(t)?.default_port ?? '')
    return (
      <>
        <Field label="Host">
          <input
            style={inputStyle}
            value={draft.HOST || ''}
            placeholder="db.internal.pharma.co"
            onChange={(e) => patch({ HOST: e.target.value })}
          />
        </Field>
        <Field label="Port">
          <input
            style={inputStyle}
            type="number"
            value={draft.PORT ?? ''}
            placeholder={portPlaceholder}
            onChange={(e) => patch({ PORT: e.target.value ? Number(e.target.value) : null })}
          />
        </Field>
        {t === 'oracle' && (
          <Field label="Connect by" full>
            <div style={{ display: 'flex', gap: 18, fontSize: 15.5 }}>
              <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="radio"
                  checked={(draft.ORACLE_CONNECT_TYPE || 'service_name') === 'service_name'}
                  onChange={() => patch({ ORACLE_CONNECT_TYPE: 'service_name' })}
                />
                Service name
              </label>
              <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="radio"
                  checked={draft.ORACLE_CONNECT_TYPE === 'sid'}
                  onChange={() => patch({ ORACLE_CONNECT_TYPE: 'sid' })}
                />
                SID
              </label>
            </div>
          </Field>
        )}
        <Field
          label={t === 'oracle'
            ? (draft.ORACLE_CONNECT_TYPE === 'sid' ? 'SID' : 'Service name')
            : 'Database'}
        >
          <input
            style={inputStyle}
            value={draft.DATABASE || ''}
            placeholder={t === 'oracle' ? 'ORCLPDB1' : 'analytics'}
            onChange={(e) => patch({ DATABASE: e.target.value })}
          />
        </Field>
        <Field label="Schema (optional)">
          <input
            style={inputStyle}
            value={draft.SCHEMA || ''}
            placeholder={t === 'postgresql' ? 'public' : ''}
            onChange={(e) => patch({ SCHEMA: e.target.value })}
          />
        </Field>
        {usernameField}
        {passwordField}
      </>
    )
  }

  // ---- test-result box (spec §5.3) ----------------------------------------

  const testBox = testResult && (
    <div
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 11,
        padding: '13px 15px', borderRadius: 10, marginTop: 16,
        background: testResult.state === 'ok'
          ? 'var(--color-success-weak)'
          : testResult.state === 'fail'
            ? 'var(--color-error-weak)'
            : 'var(--color-surface-2)',
        border: `1px solid ${testResult.state === 'ok'
          ? 'var(--color-success)'
          : testResult.state === 'fail'
            ? 'var(--color-error)'
            : 'var(--color-border)'}`,
      }}
    >
      <span
        aria-hidden
        style={{
          flexShrink: 0, width: 20, height: 20, borderRadius: '50%',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 700, color: '#fff',
          background: testResult.state === 'ok'
            ? 'var(--color-success)'
            : testResult.state === 'fail'
              ? 'var(--color-error)'
              : 'transparent',
        }}
      >
        {testResult.state === 'ok' ? '✓' : testResult.state === 'fail' ? '×' : ''}
        {testResult.state === 'testing' && (
          <span
            style={{
              width: 15, height: 15, borderRadius: '50%',
              border: '2px solid var(--color-border-strong)',
              borderTopColor: 'var(--color-accent)',
              animation: 'spin .7s linear infinite',
              display: 'inline-block',
            }}
          />
        )}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>
          {testResult.state === 'testing' ? 'Testing connection…' : testResult.headline}
        </div>
        {testResult.state !== 'testing' && testResult.message && (
          <div
            style={{
              fontSize: 14, color: 'var(--color-text-secondary)',
              fontFamily: MONO, marginTop: 3, wordBreak: 'break-word',
            }}
          >
            {testResult.message}
          </div>
        )}
      </div>
    </div>
  )

  // ---- the two panes -------------------------------------------------------

  const listPane = (
    <div
      style={{
        width: 320, flexShrink: 0, minHeight: 0,
        borderRight: '1px solid var(--color-border)',
        padding: '22px 18px', display: 'flex', flexDirection: 'column', gap: 8,
        overflowY: 'auto',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Connections</h2>
        <button
          onClick={startNew}
          style={{
            fontSize: 13, fontWeight: 600, padding: '7px 12px', borderRadius: 8,
            border: 'none', cursor: 'pointer',
            background: 'var(--color-accent)', color: '#fff',
          }}
        >
          + New
        </button>
      </div>

      {loading && connections.length === 0 ? (
        <div className="spinner" />
      ) : connections.length === 0 ? (
        <p style={{ fontSize: 15, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
          No analytics data sources yet. Add a connection to build schemas,
          business areas and visuals over an external database or warehouse
          (Snowflake, BigQuery, Dremio, SAP HANA, …).
        </p>
      ) : (
        connections.map((c) => {
          const ct = (c.DATABASE_TYPE || 'postgresql').toLowerCase()
          const isSelected = !isNew && draft?.CONNECTION_ID === c.CONNECTION_ID
          return (
            <div
              key={c.CONNECTION_ID}
              style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}
            >
            <button
              onClick={() => startEdit(c)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 11px', borderRadius: 9, cursor: 'pointer',
                textAlign: 'left', minWidth: 0, flex: 1,
                background: isSelected ? 'var(--color-accent-weak)' : 'var(--color-bg-secondary)',
                border: isSelected
                  ? '1px solid var(--color-accent)'
                  : '1px solid var(--color-border)',
                color: 'var(--color-text)',
              }}
            >
              <span
                style={{
                  width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  background: 'var(--color-surface-2)',
                  border: '1px solid var(--color-border)',
                  fontSize: 13, fontWeight: 700, fontFamily: MONO,
                  color: 'var(--color-text-secondary)',
                }}
              >
                {typeOf(ct)?.badge || 'DB'}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: 'block', fontWeight: 600, fontSize: 14,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  {c.CONNECTION_NAME || c.CONNECTION_ID}
                </span>
                <span
                  style={{
                    display: 'block', fontSize: 14, color: 'var(--color-text-faint)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  {typeLabel(c.DATABASE_TYPE)} · {c.CONNECTION_ID}
                </span>
              </span>
              <span
                title={
                  c.LAST_TEST
                    ? `Last test ${c.LAST_TEST.status === 'ok' ? 'succeeded' : 'failed'}`
                      + (c.LAST_TEST.message ? ` — ${c.LAST_TEST.message}` : '')
                    : 'Never tested'
                }
                style={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                  background: connectionHealthColor(c),
                }}
              />
            </button>
            {onAttach && (
              <button
                className="btn btn-primary btn-sm"
                onClick={() => onAttach(c)}
                title={`${attachLabel} — ${c.CONNECTION_ID}`}
                style={{ flexShrink: 0 }}
              >
                Use
              </button>
            )}
            </div>
          )
        })
      )}

      <p style={{ margin: 'auto 2px 0', paddingTop: 14, fontSize: 14, color: 'var(--color-text-faint)', lineHeight: 1.5 }}>
        Green means the last connection test succeeded. Red means it failed —
        open it to see why.
      </p>
    </div>
  )

  const formPane = (
    <div style={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto', padding: '26px 30px 60px' }}>
      {!draft ? (
        <div
          style={{
            height: '100%', minHeight: 320, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 6, textAlign: 'center',
          }}
        >
          <span
            aria-hidden
            style={{
              width: 52, height: 52, borderRadius: 12, marginBottom: 8,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--color-surface-2)',
              border: '1px solid var(--color-border)',
              fontSize: 23, color: 'var(--color-text-faint)',
            }}
          >
            ⛁
          </span>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Pick a connection to edit</div>
          <div style={{ fontSize: 15, color: 'var(--color-text-secondary)' }}>
            or create a new one to link an external database.
          </div>
        </div>
      ) : (
        <div style={{ maxWidth: 640 }}>
          <h2 style={{ margin: 0, fontSize: 21, fontWeight: 700 }}>
            {isNew ? 'New connection' : (draft.CONNECTION_NAME || draft.CONNECTION_ID)}
          </h2>
          <p style={{ margin: '6px 0 20px', fontSize: 15, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
            Fill in the connection details, then test it before saving. We never
            show saved passwords or keys back to you.
          </p>

          <div style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={labelStyle}>Display name</label>
              <input
                style={inputStyle}
                value={draft.CONNECTION_NAME || ''}
                placeholder="e.g. Pharma Data Warehouse"
                onChange={(e) => patch({ CONNECTION_NAME: e.target.value })}
              />
            </div>
            {/* Not a dropdown: 25 offered types across five sections need a
                real picker. The tile shows the current choice; the modal does
                the searching and grouping. */}
            <div style={{ width: 260, flexShrink: 0 }}>
              <label style={labelStyle}>Type</label>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                aria-haspopup="dialog"
                style={{
                  ...inputStyle, cursor: 'pointer', display: 'flex',
                  alignItems: 'center', gap: 9, textAlign: 'left',
                }}
              >
                <span aria-hidden style={{
                  flexShrink: 0, width: 24, height: 24, borderRadius: 6,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700,
                  background: 'var(--color-bg-secondary)',
                  color: 'var(--color-text-secondary)',
                }}>
                  {typeOf(t)?.badge || 'DB'}
                </span>
                <span style={{
                  flex: 1, minWidth: 0, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {typeLabel(t)}
                </span>
                <span aria-hidden style={{
                  fontSize: 13, color: 'var(--color-text-secondary)',
                }}>
                  Change
                </span>
              </button>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Connection ID</label>
            <input
              style={{ ...inputStyle, fontFamily: MONO, maxWidth: 300 }}
              value={draft.CONNECTION_ID}
              placeholder="e.g. SNOW_SALES (letters/digits/_/-)"
              disabled={!isNew}
              onChange={(e) => patch({ CONNECTION_ID: e.target.value })}
            />
            <p style={hintStyle}>
              Stable identifier schemas, business areas and visuals use to
              reference this source{isNew ? '' : ' — fixed once created'}.
            </p>
          </div>

          {isWarehouse && (
            <p style={{ ...hintStyle, margin: '0 0 16px' }}>
              Analytical sources are read-only — they can never be written to
              or used as the project&apos;s runtime database.
              {typeOf(t) && !typeOf(t)!.driver_installed && (
                <>
                  {' '}Its Python driver is <strong>not installed</strong> on
                  this server: <code>{typeOf(t)!.install_hint}</code>.
                </>
              )}
            </p>
          )}

          {t === 'gateway' && (
            <p style={{ ...hintStyle, margin: '0 0 16px' }}>
              This is a read-only source reached through the Twasta Gateway
              tunnel. The gateway runs inside the customer network and holds
              the real host and credentials — twasta only sends queries and
              gets rows back. Test verifies the gateway is online and can
              reach the database.
            </p>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px' }}>
            {renderTypeFields()}
          </div>

          {/* Type-agnostic — every database gets a statement timeout, so it
              lives outside renderTypeFields() rather than in each branch. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px', marginTop: 14 }}>
            <Field
              label="Query timeout (seconds)"
              hint={`Leave blank to use the instance default. Cannot exceed the instance maximum of ${maxQueryTimeout} seconds.`}
            >
              <input
                style={inputStyle}
                type="number"
                min={1}
                value={draft.QUERY_TIMEOUT_SECONDS ?? ''}
                placeholder="instance default"
                onChange={(e) => patch({
                  QUERY_TIMEOUT_SECONDS: e.target.value ? Number(e.target.value) : null,
                })}
              />
            </Field>
          </div>

          {testBox}

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 22, flexWrap: 'wrap' }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => void handleTest()}
              disabled={busy || !(draft.CONNECTION_ID || '').trim()}
            >
              {testing ? 'Testing…' : 'Test connection'}
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setBrowseOpen(true)}
              disabled={busy || isNew}
              title={isNew
                ? 'Save (or test) the connection first, then browse its tables'
                : 'List the tables and columns this connection can see'}
            >
              Browse tables…
            </button>
            {!isNew && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => void handleDelete()}
                disabled={busy}
                style={{ color: 'var(--color-error)' }}
              >
                Delete
              </button>
            )}
            <span style={{ flex: 1 }} />
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => { setDraft(null); setTestResult(null) }}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              className={onAttach ? 'btn btn-secondary btn-sm' : 'btn btn-primary btn-sm'}
              onClick={() => void handleSave()}
              disabled={busy || !(draft.CONNECTION_ID || '').trim()}
            >
              {saving ? 'Saving…' : 'Save connection'}
            </button>
            {onAttach && (
              <button
                className="btn btn-primary btn-sm"
                onClick={() => void handleAttach()}
                disabled={busy || !(draft.CONNECTION_ID || '').trim()}
                title="Save this connection and hand it to the agent"
              >
                {attachLabel}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )

  const hub = (
    <div
      style={{
        display: 'flex', alignItems: 'stretch', minHeight: 0,
        height: variant === 'inline' ? '100%' : '68vh',
        background: 'var(--color-bg)',
      }}
    >
      {listPane}
      {formPane}
      {draft && !isNew && (
        <BrowseTablesModal
          project={project}
          connectionId={draft.CONNECTION_ID}
          connectionName={draft.CONNECTION_NAME || draft.CONNECTION_ID}
          schema={draft.SCHEMA || undefined}
          open={browseOpen}
          onClose={() => setBrowseOpen(false)}
        />
      )}
      {confirmDelete && (
        <DeleteObjectDialog
          open
          project={project}
          objType="connection"
          objName={confirmDelete.id}
          displayName={confirmDelete.name}
          objLabel="connection"
          onClose={() => setConfirmDelete(null)}
          onDeleted={() => {
            setDraft(null)
            setTestResult(null)
            setConfirmDelete(null)
            onChanged?.()
            void refresh()
            void loadCatalog()  // "in use" counts changed
          }}
          doDelete={async () => { await deleteBiConnection(project, confirmDelete.id) }}
        />
      )}

      <DatabaseTypePickerModal
        open={pickerOpen}
        types={catalog}
        categories={categories}
        value={draft?.DATABASE_TYPE || 'postgresql'}
        loading={catalogLoading}
        error={catalogError}
        onSelect={changeType}
        onClose={() => setPickerOpen(false)}
      />
    </div>
  )

  if (variant === 'modal') {
    return (
      <Modal
        open={open}
        onClose={onClose ?? (() => undefined)}
        title={onAttach ? 'Choose the data connection' : 'Analytics data sources'}
        size="full"
      >
        {hub}
      </Modal>
    )
  }
  return hub
}
